// Plans the day: which due cards to review, which new phrases to meet, and the order
// of the session's steps. Pure functions of the stored state and the current time.

import type { Course, Item, Lesson } from '../content/types';
import type { Persisted, SessionState, Step } from '../state/model';
import { dayKey, parseDay } from './day';
import { isStoryItem } from '../stories/words';
import { cardId, dueDate, isDue, type CardRecord } from './scheduler';

/** Minutes a review takes, on average (the time estimates on Today). */
export const REVIEW_MINUTES = 0.35;

/** At most this many due cards in one day; the rest wait, most overdue first. */
export const MAX_DUE = 60;
/** The brake: no new phrases while the due cards exceed this many normal days. */
export const BRAKE_FACTOR = 1.5;
export const MIN_NORMAL_DAY = 40;

export interface DayPlan {
  day: string;
  due: CardRecord[];
  /** Due cards left for another day because of the cap or "Can't speak now". */
  waiting: number;
  newItems: Item[];
  /** Lessons whose scene runs today because their last phrases are met today (or already were). */
  sceneLessons: string[];
  braked: boolean;
  quiet: boolean;
  /** Every phrase on the learner's path has been met. */
  courseMet: boolean;
  /** No more new phrases today because today's limit is used. */
  limitReached: boolean;
  minutes: number;
}

export function normalDay(reviewsByDay: Record<string, number>, now: Date): number {
  const today = parseDay(dayKey(now));
  let total = 0;
  for (let i = 1; i <= 14; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    total += reviewsByDay[dayKey(d)] ?? 0;
  }
  return Math.max(MIN_NORMAL_DAY, total / 14);
}

export function introducedOn(state: Persisted, day: string): Set<string> {
  const items = new Set<string>();
  // Phrases kept from a story don't use up the day's new phrases.
  for (const card of Object.values(state.cards)) if (card.introduced === day && !isStoryItem(card.itemId)) items.add(card.itemId);
  return items;
}

/**
 * The learner's path: their goal's track, its lessons in its own order (Tourist before a goal is
 * chosen). New phrases and scenes come only from here; reviews cover everything already met.
 */
export function pathOf(state: Persisted, course: Course): Lesson[] {
  const track = course.tracks?.find((t) => t.id === (state.profile.goal ?? 'tourist')) ?? course.tracks?.[0];
  if (!track) return course.lessons;
  const byId = new Map(course.lessons.map((l) => [l.id, l]));
  return [...new Set(track.units.flatMap((u) => u.lessons))].flatMap((id) => byId.get(id) ?? []);
}

export function isMet(state: Persisted, itemId: string): boolean {
  return Boolean(state.cards[cardId(itemId, 'say')] || state.cards[cardId(itemId, 'hear')]);
}

/** Orders due cards: most overdue first, ties in course order, never the same phrase twice in a row. */
export function orderDue(cards: CardRecord[], course: Course): CardRecord[] {
  const orderOf = new Map(course.items.map((i) => [i.id, i.order]));
  const sorted = [...cards].sort(
    (a, b) =>
      dueDate(a).getTime() - dueDate(b).getTime() ||
      (orderOf.get(a.itemId) ?? 0) - (orderOf.get(b.itemId) ?? 0) ||
      a.kind.localeCompare(b.kind),
  );
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].itemId !== sorted[i - 1].itemId) continue;
    const swap = sorted.findIndex((c, j) => j > i && c.itemId !== sorted[i - 1].itemId);
    if (swap > 0) [sorted[i], sorted[swap]] = [sorted[swap], sorted[i]];
  }
  return sorted;
}

export function planDay(state: Persisted, course: Course, now: Date): DayPlan {
  const day = dayKey(now);
  const flags = state.profile.flags;
  const quiet = state.profile.quietDay === day;

  const dueAll = Object.values(state.cards).filter(
    (c) => !flags[c.itemId] && course.items.some((i) => i.id === c.itemId) && isDue(c, now),
  );
  const eligible = dueAll.filter((c) => !(quiet && c.kind === 'say'));
  const ordered = orderDue(eligible, course);
  const due = ordered.slice(0, MAX_DUE);

  const braked = dueAll.length > BRAKE_FACTOR * normalDay(state.profile.reviewsByDay, now);
  const extra = state.profile.extraNew?.day === day ? state.profile.extraNew.count : 0;
  const room = Math.max(0, state.settings.newPerDay + extra - introducedOn(state, day).size);
  const path = pathOf(state, course);
  const itemsById = new Map(course.items.map((i) => [i.id, i]));
  const unmet = path.flatMap((l) => l.phrases).flatMap((id) => itemsById.get(id) ?? []).filter((i) => !isMet(state, i.id) && !flags[i.id]);
  // On a "Can't speak now" day new phrases are still met, by ear; their speaking waits.
  const newItems = braked ? [] : unmet.slice(0, room);

  const metAfter = (id: string) => isMet(state, id) || newItems.some((i) => i.id === id) || Boolean(flags[id]);
  const sceneLessons = quiet
    ? []
    : path
        .filter((l) => !state.profile.lessonsDone.includes(l.id) && l.phrases.every(metAfter))
        .filter((l) => l.phrases.some((id) => !flags[id]))
        .map((l) => l.id);

  const minutes = Math.max(1, Math.round(due.length * REVIEW_MINUTES + newItems.length * 1.6 + sceneLessons.length * 2));

  return {
    day,
    due,
    waiting: dueAll.length - due.length,
    newItems,
    sceneLessons,
    braked,
    quiet,
    courseMet: unmet.length === 0,
    limitReached: !braked && room === 0 && unmet.length > 0,
    minutes,
  };
}

/** Shuffles a copy with the given random source (Fisher–Yates). */
export function shuffle<T>(list: T[], random: () => number = Math.random): T[] {
  const copy = [...list];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/** Groups of two or more phrases sharing a pattern, at least one of them new today. */
export function contrastGroups(newItems: Item[], state: Persisted, course: Course): string[][] {
  const groups: string[][] = [];
  const frames = new Set(newItems.map((i) => i.slot?.frame).filter(Boolean) as string[]);
  for (const frame of frames) {
    const members = course.items.filter(
      (i) => i.slot?.frame === frame && !state.profile.flags[i.id] && (newItems.includes(i) || isMet(state, i.id)),
    );
    if (members.length >= 2) groups.push(members.map((i) => i.id));
  }
  return groups;
}

/** Turns the day's plan into the session's steps. */
export function buildSteps(plan: DayPlan, state: Persisted, course: Course, random: () => number = Math.random): Step[] {
  const steps: Step[] = plan.due.map((c) => ({ kind: 'review', cardId: c.id }));

  const byLesson = new Map<string, Item[]>();
  for (const item of plan.newItems) byLesson.set(item.lesson, [...(byLesson.get(item.lesson) ?? []), item]);

  const scenesLeft = new Set(plan.sceneLessons);
  for (const lesson of pathOf(state, course)) {
    const items = byLesson.get(lesson.id) ?? [];
    if (items.length) {
      const started = lesson.phrases.some((id) => isMet(state, id));
      if (!started) steps.push({ kind: 'lesson-intro', lessonId: lesson.id });
      for (const item of items) steps.push({ kind: 'meet', itemId: item.id });
      // Phrases on the same pattern are told apart by ear before they are produced: hearing which
      // small word changed (έναν καφέ / ένα νερό / μια μπίρα) comes before saying it.
      for (const group of contrastGroups(items, state, course)) steps.push({ kind: 'contrast', itemIds: group });
      // The first card of each phrase: say-it for phrases you say, hear-it for phrases you hear
      // (and hear-it for all of them on a listening-only day). Shuffled so the order of meeting
      // gives no clue.
      for (const item of shuffle(items, random)) steps.push({ kind: 'first', cardId: cardId(item.id, plan.quiet ? 'hear' : item.role) });
    }
    if (scenesLeft.has(lesson.id)) {
      steps.push({ kind: 'scene', lessonId: lesson.id });
      steps.push({ kind: 'lesson-done', lessonId: lesson.id });
      scenesLeft.delete(lesson.id);
    }
  }
  return steps;
}

export function newSession(plan: DayPlan, state: Persisted, course: Course, now: Date, random?: () => number): SessionState {
  return {
    id: `${plan.day}-${now.getTime().toString(36)}`,
    day: plan.day,
    steps: buildSteps(plan, state, course, random),
    index: 0,
    met: [],
    graded: {},
    skipped: 0,
    quiet: plan.quiet,
    startedAt: now.toISOString(),
    completedAt: null,
  };
}

/** True when there is something to do today. */
export function hasWork(plan: DayPlan): boolean {
  return plan.due.length > 0 || plan.newItems.length > 0 || plan.sceneLessons.length > 0;
}
